import { Component, OnInit, Input, OnDestroy, AfterContentInit, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { BaseType, Selection, select } from 'd3-selection';
import { transition } from 'd3-transition';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { takeWhile, take, filter } from 'rxjs/operators';
import { SvgItem } from '../../classes/svg-item';
import { Guid } from 'guid-typescript';
import { Circle } from '../../types/circle';
import { Rect } from '../../types/rect';
import { Text } from '../../types/text';
import { IShape } from '../../interfaces/iShape';
import { MatSliderChange } from '@angular/material/slider';
import { Point } from '../../types/point';
import * as Hammer from 'hammerjs';
import { WheelDirection } from '../../directives/mouse-wheel.directive';
import { Image } from '../../types/image';
import { ShapeBase } from '../../types/shape.base';


const pointerStyle = 'cursor: pointer; user-select: none; text-decoration: none';

@Component({
  selector: 'ack-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.css']
})
export class ViewerComponent implements OnInit, OnDestroy, AfterContentInit {

  @Input() rawData: Observable<SvgItem<any>[]>;
  @Input() height: string;
  @Input() width: string;
  @Input() public zoom: number;
  @Input() public pan: Point;

  @Output() public shapeClick: EventEmitter<any>;
  @Output() public shapeTripleClick: EventEmitter<IShape>;
  @Output() public zoomChange: EventEmitter<number>;
  @Output() public panChange: EventEmitter<Point>;

  @ViewChild('svg', {static: true}) public svgRef: ElementRef;

  public id: string;
  public drillDownEnabled = true;
  public zoomMin = 0.5;
  public zoomMax = 4;
  public zoomDefault = 1;

  constructor(
  ) {
    this.zoom = 1;
    this.pan = {
        x: 0,
        y: 0
    };
    this.svgData = {
      rects: [],
      circles: [],
      images: [],
      labels: []
    };
    this.id = `svg_viewer_${(Math.random() * 36).toString().replace('.', '')}`;
    this.ready = new BehaviorSubject(false);
    this.shapeClick = new EventEmitter();
    this.shapeTripleClick = new EventEmitter();
    this.zoomChange = new EventEmitter();
    this.panChange = new EventEmitter();
  }

  private svg: Selection<BaseType, unknown, HTMLElement, any>;
  private ready: BehaviorSubject<boolean>;
  private svgData: {['rects']: Rect[], ['circles']: Circle[], ['images']: Image[], ['labels']: Text[]};
  private isDestroying = false;


  ngOnInit() {

    const readyObs = this.ready.pipe(
      filter(val => val),
      take(1));

    const dataObs = this.rawData.pipe(
      takeWhile(() => !this.isDestroying)
    );

    combineLatest(readyObs, dataObs)
    .subscribe(([ready, data]) => {

      if (!ready) { return; }

      this.pan = {x: 0, y: 0};

      this.svgData = {
        rects: [],
        circles: [],
        images: [],
        labels: []
      };

      // convert raw data to svg data
      data.forEach((item, index) => {

        const point = item.getCoordinate();

        switch (item.getShape()) {
          case 'rect':
            this.svgData.rects.push({
              originalData: item.getData(),
              x: point.x,
              y: point.y,
              width: item.getWidth(),
              height: item.getheight(),
              fill: item.getFill(),
              borderColor: item.getBorderColor(),
              borderSize: item.getBorderSize(),
              rotation: item.getRotation(),
              blink: item.getIsBlink()
            });
            break;

          case 'circle':
            this.svgData.circles.push({
              originalData: item.getData(),
              x: point.x,
              y: point.y,
              fill: item.getFill(),
              radius: item.getRadius(),
              borderColor: item.getBorderColor(),
              borderSize: item.getBorderSize(),
              rotation: item.getRotation(),
              blink: item.getIsBlink()
            });
            break;

          case 'image':
            this.svgData.images.push({
              originalData: item.getData(),
              height: item.getheight(),
              width: item.getWidth(),
              x: point.x,
              y: point.y,
              imageUrl: item.getImageUrl(),
              rotation: item.getRotation()
            });
            break;

          case 'text':
            this.svgData.labels.push({
              originalData: item.getData(),
              fontColor: item.getFontColor(),
              fontSize: item.getFontSize(),
              fontWeight: item.getFontWeight(),
              x: point.x,
              y: point.y,
              text: item.getText(),
              rotation: item.getRotation()
            });
            break;

        }

      });

      // draw items on svg
      this.draw();

    });

    // enable pinch to zoom detenction
    const hammer = new Hammer(this.svgRef.nativeElement);
    hammer.get('pinch').set({ enable: true });

    hammer.on('pinch', e => {

      const sign = e.additionalEvent === 'pinchout' ? 1 : -1;

      let zoom = this.zoom + ((e.scale / 25) * sign);

      if (zoom > this.zoomMax) {
        zoom = this.zoomMax;
      }

      if (zoom < this.zoomMin) {
        zoom = this.zoomMin;
      }

      this.zoom = zoom;
      this.zoomChange.emit(this.zoom);

    });

  }

  ngOnDestroy() {
    this.isDestroying = true;
  }

  ngAfterContentInit() {

    setTimeout(() => {
      this.svg = select(`#${this.id}`);
      this.ready.next(true);
    }, 150);
  }

  public onShapeClick(event: IShape) {

    if (event && event.originalData) {
      this.shapeClick.emit(event.originalData);
    }

  }

  public onShapeTripleClick(event: IShape) {

    if (event && event.originalData) {
      this.shapeTripleClick.emit(event.originalData);
    }

  }

  public sliderChange(evt: MatSliderChange) {
    this.zoom = evt.value;
    this.zoomChange.emit(this.zoom);
  }


  public onPanChange(evt: Point) {
    this.drillDownEnabled = false;
    this.pan.x += (evt.x);
    this.pan.y += (evt.y);
    this.panChange.emit(this.pan);
  }

  public wheelMove(direction: WheelDirection) {

    const zoom = direction === 'up' ? this.zoom + 0.1 : this.zoom - 0.1;

    if (zoom < this.zoomMin) {
      this.zoom = this.zoomMin;
      return;
    }

    if (zoom > this.zoomMax) {
      this.zoom = this.zoomMax;
      return;
    }

    this.zoom = zoom;
    this.zoomChange.emit(this.zoom);
  }

  public get transform() {

    let translate = '';
    let zoom = 'scale(1)';

    if (this.pan.x && this.pan.y) {
      translate = `translate(${this.pan.x * -1}px, ${this.pan.y * -1}px)`;
    }

    if (this.zoom) {
      zoom = `scale(${this.zoom})`;
    }

    return {
      transform: `${translate} ${zoom}`
    };

  }

  private draw() {

    const detectTransform = (el: ShapeBase) => {
      if (el.rotation) {
        return `rotate(${el.rotation} ${el.x},${el.y})`;
      }
    };

    // ensure that draw is cleared before drawing
    this.svg.selectAll('*').remove();

    // draw rects
    this.svg.selectAll('rect')
      .data(this.svgData.rects)
      .enter()
      .append('rect')
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('width', (d) => d.width)
      .attr('height', (d) => d.height)
      .attr('fill', (d) => d.fill)
      .attr('stroke', (d) => d.borderColor)
      .attr('stroke-width', d => d.borderSize)
      .attr('id', () => `rect_${Guid.create().toString()}`)
      .attr('style', pointerStyle)
      .attr('transform', (d) => {
        return detectTransform(d);
      })
      .attr('blink', (d) => d.blink);


    // draw circles
    this.svg.selectAll('circle')
      .data(this.svgData.circles)
      .enter()
      .append('circle')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', (d) => d.radius)
      .attr('fill', (d) => d.fill)
      .attr('stroke', (d) => d.borderColor)
      .attr('stroke-width', d => d.borderSize)
      .attr('id', () => `circle_${Guid.create().toString()}`)
      .attr('style', pointerStyle)
      .attr('transform', (d) => {
        return detectTransform(d);
      })
      .attr('blink', (d) => d.blink);


    // draw images
    this.svg.selectAll('image')
        .data(this.svgData.images)
        .enter()
        .append('image')
        .attr('x', (d) => d.x)
        .attr('y', (d) => d.y)
        .attr('width', (d) => d.width)
        .attr('height', (d) => d.height)
        .attr('id', () => `img_${Guid.create().toString()}`)
        .attr('style', pointerStyle)
        .attr('href', (d) => d.imageUrl)
        .attr('transform', (d) => {
          return detectTransform(d);
        });


    // draw labels
    this.svg.selectAll('g')
    .data(this.svgData.labels)
    .enter()
    .append('g')
    .attr('id', () => `g_${Guid.create().toString()}`)
    .selectAll('text')
    .data(d => {

      const paragraphs = d.text ? d.text.split('\n') : [];

      const result: any[] = [];

      let i = 0;

      for (const paragraph of paragraphs) {
        result.push({
          ...d,
          i,
          text: paragraph
        });

        i++;
      }

      return result;
    })
    .enter()
    .append('text')
    .attr('x', d => d.x)
    .attr('y', d => d.y + (d.i * d.fontSize))
    .attr('fill', d => d.fontColor)
    .attr('font-family', 'Roboto, monospace')
    .attr('font-size', d => d.fontSize)
    .attr('id', () => `text_${Guid.create().toString()}`)
    .attr('style', pointerStyle)
    .attr('font-weight', d => d.fontWeight)
    .attr('transform', (d) => {
      return detectTransform(d);
    })
    .text((d) => d.text);


    // add blink elements
    this.svg.selectAll('[blink=true]')
    .append('animate')
    .attr('attributeType', 'XML')
    .attr('attributeName', 'fill')
    .attr('from', (d: any) => d.fill)
    .attr('to', 'darkgray')
    .attr('dur', '1s')
    .attr('repeatCount', 'indefinite');

  }


}
